// Outcomes for coordinator review of a progress note or significant communication.
//
// These MUST equal the CHECK constraint on clinical_note_reviews.outcome in
// supabase/migrations/20270885000050_clinical_note_review_queue.sql. A test
// parses the migration and asserts the two lists are identical, so drift fails
// the build rather than surfacing as a 23514 at the point of care.
//
// Unlike the communication vocabulary — where a DB CHECK was deliberately
// deferred because the mobile app is a shipped binary that could violate it —
// this is a new table with no legacy rows and no mobile writer, so the
// constraint is enforced in the database from the outset.
//
// There is deliberately no DISMISS. Clinical items close only by recording what
// was decided; a dismiss button inherited from the funding-exception queue would
// mean "cleared clinical risk without reading".

export const CLINICAL_NOTE_REVIEW_OUTCOMES = [
  'ACKNOWLEDGED',
  'FOLLOW_UP_REQUIRED',
  'ESCALATED_CLINICAL',
  'ESCALATED_COMPLAINT',
  'ESCALATED_INCIDENT',
  'NO_ACTION_REQUIRED',
] as const;

export type ClinicalNoteReviewOutcome = (typeof CLINICAL_NOTE_REVIEW_OUTCOMES)[number];

/** Outcomes that require the reviewer to say what they escalated, and to whom. */
export const CLINICAL_NOTE_REVIEW_ESCALATIONS = [
  'ESCALATED_CLINICAL',
  'ESCALATED_COMPLAINT',
  'ESCALATED_INCIDENT',
] as const satisfies readonly ClinicalNoteReviewOutcome[];

export function isClinicalNoteReviewOutcome(value: unknown): value is ClinicalNoteReviewOutcome {
  return (
    typeof value === 'string' &&
    (CLINICAL_NOTE_REVIEW_OUTCOMES as readonly string[]).includes(value)
  );
}

export function requiresReviewNote(outcome: string): boolean {
  return (CLINICAL_NOTE_REVIEW_ESCALATIONS as readonly string[]).includes(outcome);
}

/** Subject kinds surfaced by vw_notes_pending_review. */
export const CLINICAL_NOTE_REVIEW_SUBJECT_KINDS = ['CASE_NOTE', 'COMMUNICATION'] as const;
export type ClinicalNoteReviewSubjectKind = (typeof CLINICAL_NOTE_REVIEW_SUBJECT_KINDS)[number];

/**
 * Outcomes that raise a regulated workflow task, and so must reach an owner.
 *
 * MUST equal the branch in record_clinical_note_review_controlled
 * (20271030000001). Before that migration the escalation outcomes wrote a row
 * and nothing else — no task, no notification — so an escalation reached
 * nobody. A test asserts this list against the migration, because the UI uses
 * it to decide whether a missing follow_up_task_id is a warning worth showing
 * or an ordinary absence.
 *
 * ACKNOWLEDGED and NO_ACTION_REQUIRED are excluded deliberately: they are a
 * coordinator saying "read, nothing to do". Giving those a task would flood the
 * inbox and teach people to close tasks unread.
 */
export const CLINICAL_NOTE_REVIEW_TASK_OUTCOMES = [
  'FOLLOW_UP_REQUIRED',
  'ESCALATED_CLINICAL',
  'ESCALATED_COMPLAINT',
  'ESCALATED_INCIDENT',
] as const satisfies readonly ClinicalNoteReviewOutcome[];

/** True when this outcome should have produced a follow-up task. */
export function outcomeRaisesTask(outcome: string): boolean {
  return (CLINICAL_NOTE_REVIEW_TASK_OUTCOMES as readonly string[]).includes(outcome);
}
